import {
  useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import { useApp } from 'hooks';
import { paymentMethodTypes, paymentStatuses } from 'constants/service-plans';
import { formatDateTime } from 'helpers/datetime.helper';
import { getChargeReceiptPdfAsync, getPaymentsHistory, PAYMENTS_HISTORY_PAGE_SIZE } from 'api';
import ActionMenu from 'components/ui-kit/action-menu';
import GenericTable from 'components/ui-kit/generic-table';
import LabelMark from 'components/ui-kit/label-mark';
import Money from 'components/ui-kit/money';
import Pagination from 'components/ui-kit/pagination';
import PaymentsHistoryLoader from './loader';
import * as S from './styles';

const PAID_STATUS = 2;

const relatedEntityLabels = {
  0: 'Job',
  1: 'Estimate',
  2: 'Service plan',
};

const relatedEntityColors = {
  0: 'green',
  1: 'yellow',
  2: 'metallic',
};

const paymentStatusColors = {
  0: 'metallic',
  1: 'yellow',
  2: 'green',
  3: 'red',
  4: 'metallic',
  5: 'red',
};

const EllipsisText = ({ text }) => {
  const ref = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = ref.current;
    setIsOverflowing(!!el && el.scrollWidth > el.clientWidth);
  }, [text]);

  return (
    <S.Ellipsis ref={ref} title={isOverflowing ? text : undefined}>
      {text}
    </S.Ellipsis>
  );
};

EllipsisText.propTypes = {
  text: PropTypes.string,
};

EllipsisText.defaultProps = {
  text: '',
};

export default function PaymentsHistory() {
  const { portal } = useApp();
  const portalId = portal?.id;
  const [page, setPage] = useState(1);

  const {
    data, isLoading, isError,
  } = useQuery(['payments-history', portalId, page], getPaymentsHistory, {
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!portalId,
    keepPreviousData: true,
  });

  if (isError) {
    return null;
  }

  const response = data?.data || {};
  const items = response.items || [];
  const total = response.total || 0;
  const totalPages = Math.ceil(total / PAYMENTS_HISTORY_PAGE_SIZE);

  const dateCell = (value, index) => (
    <S.Cell key={index}>{formatDateTime(value)}</S.Cell>
  );

  const typeCell = (value, index, item) => (
    <S.Cell key={index} $align="left">
      <S.Tags>
        <LabelMark
          text={relatedEntityLabels[item.relatedEntityType] || ''}
          color={relatedEntityColors[item.relatedEntityType] || 'metallic'}
        />
        {item.isDeposit && <LabelMark text="Deposit" color="blue" />}
      </S.Tags>
    </S.Cell>
  );

  const descriptionCell = (value, index) => (
    <S.Cell key={index} $align="left">
      <EllipsisText text={value || ''} />
    </S.Cell>
  );

  const methodCell = (value, index) => (
    <S.Cell key={index} $align="left">
      <EllipsisText text={paymentMethodTypes[value] || ''} />
    </S.Cell>
  );

  const statusCell = (value, index) => (
    <S.Cell key={index} $align="left">
      <S.StatusWrapper>
        <LabelMark text={paymentStatuses[value]} color={paymentStatusColors[value]} />
      </S.StatusWrapper>
    </S.Cell>
  );

  const amountCell = (value, index, item) => (
    <S.Cell key={index} isBold>
      <S.Amount $refunded={!!item.refundDate}>
        <Money amount={value} />
      </S.Amount>
    </S.Cell>
  );

  const actionsCell = (value, index, item) => {
    const canDownloadReceipt = item.paymentStatus === PAID_STATUS;

    return (
      <S.Cell key={index} $align="center">
        <S.Actions>
          <ActionMenu>
            {canDownloadReceipt ? (
              <a href={getChargeReceiptPdfAsync(portalId, item.id)} target="_blank" rel="noreferrer">
                Download Receipt
              </a>
            ) : (
              <button type="button" disabled>Download Receipt</button>
            )}
          </ActionMenu>
        </S.Actions>
      </S.Cell>
    );
  };

  const columns = [
    {
      name: 'paymentDate', label: 'Date', component: dateCell, width: '20%',
    },
    {
      name: 'relatedEntityType', label: 'Type', component: typeCell, align: 'left', width: '19%',
    },
    {
      name: 'relatedEntityNumber', label: 'Description', component: descriptionCell, align: 'left', width: '18%',
    },
    {
      name: 'paymentMethod', label: 'Method', component: methodCell, align: 'left', width: '9%',
    },
    {
      name: 'paymentStatus', label: 'Status', component: statusCell, align: 'left', width: '14%',
    },
    {
      name: 'totalAmount', label: 'Amount', component: amountCell, width: '12%',
    },
    {
      name: 'id', label: '', component: actionsCell, align: 'center', width: '8%',
    },
  ];

  const renderContent = () => {
    if (isLoading) {
      return <PaymentsHistoryLoader />;
    }

    if (total === 0) {
      return <S.Empty>No payments yet</S.Empty>;
    }

    return (
      <>
        <S.Table>
          <GenericTable items={items} columns={columns} fixed />
        </S.Table>
        <S.Pagination>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </S.Pagination>
      </>
    );
  };

  return (
    <S.Section>
      <S.SectionTitle>Payment history</S.SectionTitle>
      {renderContent()}
    </S.Section>
  );
}
